import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { InAppBrowser as CapInAppBrowser, ToolBarType } from '@capgo/inappbrowser';
export { ToolBarType };
import { Observable, Subject } from 'rxjs';

/**
 * Simplified options for compatibility with existing code.
 * We ignore most Cordova-specific keys as requested.
 */
export interface InAppBrowserOptions {
  clearCache?: boolean;
  clearCookies?: boolean;
  toolbarType?: ToolBarType;
  [key: string]: any;
}

export interface InAppBrowserEvent {
  type: string;
  url?: string;
  code?: number;
  message?: string;
  data?: any;
}

export class InAppBrowserObject {
  private events$ = new Subject<InAppBrowserEvent>();
  private listenerHandles: any[] = [];

  constructor(
    private url: string,
    private target: string,
    private options: InAppBrowserOptions | string,
  ) {
    this.open();
  }

  private async open() {
    const platform = Capacitor.getPlatform();
    try {
      if (typeof this.options === 'object') {
        if (this.options.clearCache) {
          try {
            await CapInAppBrowser.clearCache();
          } catch (e) {
            console.error('Error clearing cache', e);
          }
        }
        if (this.options.clearCookies) {
          try {
            await CapInAppBrowser.clearAllCookies();
          } catch (e) {
            console.error('Error clearing cookies', e);
          }
        }
      }

      const isSystem = this.target === '_system';
      console.log("🚀 ~ InAppBrowserObject ~ open ~ isSystem:", isSystem)
      if (isSystem) {
        if (platform !== 'web') {
          await (window as any)?.cordova?.InAppBrowser?.open(this.url, '_system');
        } else {
          window.open(this.url, '_self');
        }
      } else {
        // Determine toolbar type
        let toolbarType = ToolBarType.COMPACT;
        if (typeof this.options === 'object' && this.options.toolbarType) {
          toolbarType = this.options.toolbarType;
        }

        // Use WebView for internal browsing
        await CapInAppBrowser.openWebView({
          url: this.url,
          toolbarType: toolbarType,
          visibleTitle: false,
          title: '',
          headers: {
            'user-agent':
              platform === 'ios'
                ? `Mozilla/5.0 (iPhone; CPU iPhone OS16_5_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/16.5.2 Mobile/15E148 Safari/604.1`
                : 'Mozilla/5.0 (Linux; Android 10; K) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/115.0.0.0 Mobile Safari/537.36',
          },
        });

        // Setup event listeners
        await this.setupEventListeners();
      }
    } catch (err) {
      console.error('Error opening InAppBrowser', {
        error: err,
        url: this.url,
        target: this.target,
      });
      this.events$.next({ type: 'exit', url: this.url });
      this.cleanup();
    }
  }

  private async setupEventListeners() {
    // Page load started (approximated by url change)
    this.listenerHandles.push(
      await CapInAppBrowser.addListener('urlChangeEvent', (res: any) => {
        this.events$.next({ type: 'loadstart', url: res.url });
        this.url = res.url;
      }),
    );

    // Page load stopped
    this.listenerHandles.push(
      await CapInAppBrowser.addListener('browserPageLoaded', () => {
        this.events$.next({ type: 'loadstop', url: this.url });
      }),
    );

    // Page load error
    this.listenerHandles.push(
      await CapInAppBrowser.addListener('pageLoadError', () => {
        this.events$.next({ type: 'loaderror', url: this.url });
      }),
    );

    // Browser closed
    this.listenerHandles.push(
      await CapInAppBrowser.addListener('closeEvent', async (res: any) => {
        this.cleanup();
        if (typeof this.options === 'object') {
          if (this.options.clearCache) {
            try {
              await CapInAppBrowser.clearCache();
            } catch (e) {
              console.error('InAppBrowser: Failed to clear cache on close', e);
            }
          }
          if (this.options.clearCookies) {
            try {
              await CapInAppBrowser.clearAllCookies();
            } catch (e) {
              console.error('InAppBrowser: Failed to clear cookies on close', e);
            }
          }
        }
        this.events$.next({ type: 'exit', url: res?.url || this.url });
      }),
    );

    // Communication from webview
    this.listenerHandles.push(
      await CapInAppBrowser.addListener('messageFromWebview', (res: any) => {
        let messageData = res.detail || res.data || res.message || res;

        // Handle stringified JSON from old-style postMessage or certain platforms
        if (typeof messageData === 'string') {
          try {
            messageData = JSON.parse(messageData);
          } catch (e) {
            console.error('Failed to parse message data as JSON:', messageData, e);
            // Not JSON, leave as string
          }
        }

        this.events$.next({ type: 'message', data: messageData });
      }),
    );
  }

  private cleanup() {
    this.listenerHandles.forEach((h) => h.remove());
    this.listenerHandles = [];
  }

  public close(): void {
    this.cleanup();
    CapInAppBrowser.close({ isAnimated: false });
    this.events$.next({ type: 'exit', url: this.url });
  }

  public executeScript(script: { code: string }): Promise<any> {
    return CapInAppBrowser.executeScript({ code: script.code });
  }

  public insertCSS(css: { code?: string; file?: string }): Promise<any> {
    if (css.code) {
      const code = `
        var style = document.createElement('style');
        style.innerHTML = \`${css.code}\`;
        document.head.appendChild(style);
      `;
      return CapInAppBrowser.executeScript({ code });
    }
    return Promise.resolve();
  }

  public on(event: string): Observable<InAppBrowserEvent> {
    return new Observable((observer) => {
      const sub = this.events$.subscribe((e) => {
        if (e.type === event) {
          observer.next(e);
        }
      });
      return () => sub.unsubscribe();
    });
  }
}

@Injectable({
  providedIn: 'root',
})
export class InAppBrowser {
  /**
   * Main entry point to create a browser instance.
   * Target '_system' opens in system browser.
   * Target '_blank' or '_self' opens in internal webview.
   */
  create(
    url: string,
    target: string = '_self',
    options: InAppBrowserOptions | string = {},
  ): InAppBrowserObject {
    return new InAppBrowserObject(url, target, options);
  }

  // Helper methods that can be called directly
  async clearCache() {
    await CapInAppBrowser.clearCache();
  }
  async clearCookies() {
    await CapInAppBrowser.clearAllCookies();
  }
}
